import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { apiGet, fetchFees, refreshFees } from "../services/api";
import {
  getSingleSeaterRequestStatus,
  submitSingleSeaterRequest,
} from "../services/hostelApi";
import { Link, useNavigate } from "react-router-dom";
import { Home, User, Book, RefreshCw, CalendarDays } from "lucide-react";
import Footer from "./Footer";

export default function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [feesData, setFeesData] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [feesError, setFeesError] = useState("");
  const [hasCurrentSession, setHasCurrentSession] = useState(true);

  const [bookingSchedule, setBookingSchedule] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(true);
  const [showIncompleteProfileModal, setShowIncompleteProfileModal] = useState(false);
  const [blankFieldsList, setBlankFieldsList] = useState([]);
  
  // Track profile completeness
  const [isProfileComplete, setIsProfileComplete] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
  async function fetchData() {
    try {
      setLoading(true);
      const res = await apiGet("student", token);
      if (res.error) setError(res.error);
      else {
        setStudentData(res);
        await checkProfileStatus();
        await loadFees();
        await fetchBookingSchedule();
      }
    } catch (err) {
      setError("Unable to fetch student data.");
    } finally {
      setLoading(false);
    }
  }
  fetchData();
}, [token]);

  // Check if profile has blank fields on initial load
  const checkProfileStatus = async () => {
    try {
      const result = await apiGet("getBlankFields", token);
      if (result?.blank_fields && Array.isArray(result.blank_fields) && result.blank_fields.length > 0) {
        setBlankFieldsList(result.blank_fields);
        setIsProfileComplete(false);
      } else {
        setBlankFieldsList([]);
        setIsProfileComplete(true);
      }
    } catch (err) {
      console.error("Error checking profile status:", err);
    }
  };

  const processSessionData = (data) => {
    if (data.success && data.sessions && data.sessions.length > 0) {
      const currentSession = data.sessions.find(
        (s) => s.session_name === "2026-2027" || s.session_name === "2026-27"
      );
      if (currentSession) {
        setHasCurrentSession(true);
        setFeesData({ ...data, sessions: [currentSession] });
      } else {
        setHasCurrentSession(false);
        setFeesData(null);
      }
    } else {
      setHasCurrentSession(false);
      setFeesData(null);
    }
  };

  async function loadFees() {
    setFeesLoading(true);
    setFeesError("");
    try {
      const data = await fetchFees(token);
      if (data.error) {
        setFeesError(data.error);
      } else {
        processSessionData(data);
      }
    } catch (err) {
      setFeesError("Unable to fetch fees data.");
    } finally {
      setFeesLoading(false);
    }
  }

  async function handleRefreshFees() {
    setFeesLoading(true);
    setFeesError("");
    try {
      const data = await refreshFees(token);
      if (data.error) {
        setFeesError(data.error);
      } else {
        processSessionData(data);
      }
    } catch (err) {
      setFeesError("Unable to refresh fees data.");
    } finally {
      setFeesLoading(false);
    }
  }

  async function fetchBookingSchedule() {
    setBookingLoading(true);
    try {
      const res = await apiGet("booking_schedule", token);
      if (res.success) {
        setBookingSchedule(res.schedule);
      }
    } catch (e) {
      console.error("Booking fetch error", e);
    }
    setBookingLoading(false);
  }

  const getImageUrl = () => {
    if (!studentData) return "";
    const candidateId = studentData.profile?.CandidateId;
    const photograph = studentData.profile?.photograph;
    if (candidateId) {
      return `https://pravesh.gbu.ac.in/app/userimages/${candidateId}.jpg`;
    }
    if (photograph) {
      let cleanedPath = photograph.replace(/^(\.\.\/)+/, "").replace(/^\/+/, "");
      return `https://hostels.gbu.ac.in/ohms/${cleanedPath}`;
    }
    return "";
  };

  const showSingleSeaterRequest =
    studentData &&
    studentData.profile?.gender === "F" &&
    studentData.student?.enrollment_id?.startsWith("26") &&
    !studentData.allocation;

 const getBookingInfo = () => {
    if (!studentData || !bookingSchedule || !hasCurrentSession) return null;

    // Check if room is already allocated/booked
    const isBooked = Boolean(studentData.allocation);

    const isFirstYear = studentData.student.enrollment_id?.startsWith("26");
    const schedule = bookingSchedule.find(
      (b) => b.student_type === (isFirstYear ? "first_year" : "existing")
    );

    if (!schedule) return null;

    const now = new Date();
    const bookingStart = new Date(schedule.start_datetime);
    const isEnabled = schedule.is_enabled;

    const isClearedByFees = feesData?.sessions?.[0]?.allowed_to_allot_hostel === 1;
    const canBook = isEnabled && now >= bookingStart && isClearedByFees && !isBooked;

    return {
      isBooked,
      canBook,
      bookingStart,
      isEnabled,
    };
  };

  const handleBookRoomClick = async (e) => {
    e.preventDefault();
    if (!isProfileComplete) {
      setShowIncompleteProfileModal(true);
      return;
    }
    navigate("/roombooking");
  };

  return (
    <>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onLogout={onLogout}
        />

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="flex-1 flex flex-col md:ml-64">
          <Topbar
            setSidebarOpen={setSidebarOpen}
            enrollment={studentData?.student?.enrollment_id}
          />

          <main className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading && <p className="text-gray-500">Loading...</p>}
            {error && <p className="text-red-600">{error}</p>}

            {!loading && !error && studentData && (
              <>
                {/* Welcome Card */}
                <div className="bg-white rounded-xl shadow flex flex-col md:flex-row items-center p-6">
                  <img
                    src={getImageUrl()}
                    alt="Student Photograph"
                    className="w-24 h-24 object-cover rounded-full border-2 border-blue-200 shadow mb-4 md:mb-0 md:mr-6"
                    onError={(e) => {
                      e.target.src = "/placeholder-avatar.png";
                    }}
                  />
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-blue-700 mb-1">
                      Welcome, {studentData.student.name || "Student"}
                    </h2>
                    <p className="text-gray-600">
                      Enrollment No:{" "}
                      <span className="font-mono">
                        {studentData.student.enrollment_id}
                      </span>
                    </p>
                    <p className="text-gray-600">
                      Roll No: {studentData.student.roll_number}
                    </p>
                    <p className="mt-2">
                      <span
                        className={`inline-block px-2 py-1 text-xs rounded ${
                          studentData.student.document_status === "approved"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        Document Status: {studentData.student.document_status}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Summary Sections */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <DashboardCard
                    icon={<User className="w-5 h-5 text-blue-500" />}
                    title="Personal Info"
                    content={
                      <>
                        <InfoLine label="Email" value={studentData.student.email} />
                        <InfoLine label="Mobile" value={studentData.student.mobile} />
                      </>
                    }
                  />

                  <DashboardCard
                    icon={<Book className="w-5 h-5 text-indigo-500" />}
                    title="Programme Details"
                    content={
                      <>
                        <p className="text-gray-700 font-medium">
                          {studentData.profile?.ProgrammeName || "N/A"}
                        </p>
                        <p className="text-gray-500">
                          {studentData.profile?.department}
                        </p>
                        <p className="text-gray-500">
                          {studentData.profile?.school}
                        </p>
                      </>
                    }
                  />

                  <DashboardCard
                    icon={<Home className="w-5 h-5 text-emerald-500" />}
                    title="Hostel Allocation"
                    content={
                      studentData.allocation ? (
                        <>
                          <InfoLine label="Hostel" value={studentData.allocation.hostel_name} />
                          <InfoLine label="Room" value={studentData.allocation.room_number} />
                          <InfoLine label="Status" value={studentData.allocation.status} />
                        </>
                      ) : studentData.no_dues && studentData.no_dues.last_hostel_name ? (
                        <>
                          <p className="text-gray-500 mb-1">No current allocation.</p>
                          <InfoLine label="Last Hostel" value={studentData.no_dues.last_hostel_name} />
                          <InfoLine label="No-Dues Status" value={studentData.no_dues.status} />
                          {studentData.no_dues.no_dues_date && (
                            <InfoLine label="No-Dues Date" value={studentData.no_dues.no_dues_date} />
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500">No current allocation.</p>
                      )
                    }
                  />
                </div>

                {/* Fees Section */}
                <div className="bg-white rounded-xl shadow p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border border-blue-100">
                  <div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-5 w-5 text-blue-600" />
                      <h3 className="text-xl font-semibold text-blue-800">
                        Hostel Leave Application
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mt-1">
                      Apply for leave, calculate total days, and receive a unique application ID.
                    </p>
                  </div>
                  <Link
                    to="/leave-application"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition font-medium"
                  >
                    <CalendarDays className="h-4 w-4" />
                    Apply Leave
                  </Link>
                </div>

                {/* Fees Section */}
                <div className="bg-white rounded-xl shadow p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                      Fees Summary (Academic Year 2026-27)
                    </h3>
                    <button
                      onClick={handleRefreshFees}
                      disabled={feesLoading}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {feesLoading ? "Refreshing..." : "Sync Latest"}
                    </button>
                  </div>

                  {feesLoading && <p className="text-gray-500">Loading fees data...</p>}
                  {feesError && <p className="text-red-600">{feesError}</p>}

                  {!hasCurrentSession && !feesLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
                      <p className="text-red-800 font-medium text-base">
                        ❌ Fee details for the academic session 2026-27 are missing from your profile.
                      </p>
                      <p className="text-gray-600 text-sm mt-1">
                        Please contact the University Administration immediately to update your records.
                      </p>
                    </div>
                  )}

                  {hasCurrentSession && feesData && feesData.sessions.length > 0 && (
                    <div className="space-y-6">
                      {feesData.sessions
                        .filter(
                          (session) =>
                            session.session_name === "2026-2027" ||
                            session.session_name === "2026-27"
                        )
                        .map((session, idx) => (
                          <div
                            key={idx}
                            className="border rounded-xl p-5 bg-white shadow hover:shadow-md transition"
                          >
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="text-lg font-semibold text-blue-700">
                                Session: {session.session_name}
                              </h4>
                              <span
                                className={`text-sm px-3 py-1 rounded-full ${
                                  session.allowed_to_allot_hostel
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {session.allowed_to_allot_hostel
                                  ? "Eligible for Hostel Allotment"
                                  : "Not Eligible for Hostel Allotment"}
                              </span>
                            </div>

                            <table className="w-full text-sm border border-gray-200 rounded overflow-hidden">
                              <thead className="bg-gray-100 text-gray-700">
                                <tr>
                                  <th className="px-4 py-2 text-left">Fees Head</th>
                                  <th className="px-4 py-2 text-right">Total Fees</th>
                                  <th className="px-4 py-2 text-right">Paid Fees</th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-800">
                                <tr className="border-t">
                                  <td className="px-4 py-2">Academic Fees</td>
                                  <td className="px-4 py-2 text-right">₹ {session.academic_fees_total || 0}</td>
                                  <td className="px-4 py-2 text-right">₹ {session.academic_fees_paid || 0}</td>
                                </tr>
                                <tr className="border-t">
                                  <td className="px-4 py-2">Hostel Fees</td>
                                  <td className="px-4 py-2 text-right">₹ {session.hostel_fees_total || 0}</td>
                                  <td className="px-4 py-2 text-right">₹ {session.hostel_fees_paid || 0}</td>
                                </tr>
                                <tr className="border-t">
                                  <td className="px-4 py-2">Mess Fees</td>
                                  <td className="px-4 py-2 text-right">₹ {session.mess_fees_total || 0}</td>
                                  <td className="px-4 py-2 text-right">₹ {session.mess_fees_paid || 0}</td>
                                </tr>
                                <tr className="border-t">
                                  <td className="px-4 py-2">Examination Fees</td>
                                  <td className="px-4 py-2 text-right">₹ {session.exam_fees_total || 0}</td>
                                  <td className="px-4 py-2 text-right">₹ {session.exam_fees_paid || 0}</td>
                                </tr>
                                <tr className="border-t">
                                  <td className="px-4 py-2">Library Fees</td>
                                  <td className="px-4 py-2 text-right">₹ {session.library_fees_total || 0}</td>
                                  <td className="px-4 py-2 text-right">₹ {session.library_fees_paid || 0}</td>
                                </tr>
                                <tr className="border-t">
                                  <td className="px-4 py-2">Sports Fees</td>
                                  <td className="px-4 py-2 text-right">₹ {session.sports_fees_total || 0}</td>
                                  <td className="px-4 py-2 text-right">₹ {session.sports_fees_paid || 0}</td>
                                </tr>

                                {session.has_special_permission === 1 && (
                                  <tr className="border-t bg-amber-50/50">
                                    <td className="px-4 py-2 text-amber-800 font-medium">Special Permission</td>
                                    <td className="px-4 py-2 text-right text-amber-800" colSpan="2">
                                      {session.special_permission_expiry
                                        ? `Approved (Valid until ${session.special_permission_expiry})`
                                        : "Approved"}
                                    </td>
                                  </tr>
                                )}

                                <tr className="border-t bg-gray-50">
                                  <td className="px-4 py-2 font-semibold text-blue-700">Allowed Room Type</td>
                                  <td className="px-4 py-2 text-right font-medium" colSpan="2">
                                    {session.allowed_hostel_seater === "double"
                                      ? "Double Seater"
                                      : "Single Seater"}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {showSingleSeaterRequest && hasCurrentSession && (
                  <div className="bg-white rounded-xl shadow p-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-1">
                      Single Seater Upgrade Request:
                    </h3>
                    <p className="text-gray-500 mb-3">
                      It may take up to 24-48 hours to get updates on your request.
                    </p>
                    <SingleSeaterRequest />
                  </div>
                )}

                {/* Booking Section */}
                {hasCurrentSession && (
                  bookingLoading ? (
                    <p className="text-gray-500">Checking booking availability...</p>
                  ) : (
                    (() => {
                      const info = getBookingInfo();
                      if (!info) return null;

                      return (
                       <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl shadow p-6 flex flex-col md:flex-row justify-between items-center gap-4 border border-blue-100">
                          <div>
                            <h3 className="text-xl font-semibold text-blue-800">
                              Hostel Room Booking
                            </h3>
                            {info.isBooked ? (
                              <p className="text-green-700 text-sm font-medium mt-1">
                                🎉 Your room is already booked for this session!
                              </p>
                            ) : !info.isEnabled ? (
                              <p className="text-gray-600 text-sm mt-1">
                                Booking is currently <span className="text-red-600 font-medium">disabled</span> by Hostel Administration.
                              </p>
                            ) : info.canBook ? (
                              <p className="text-gray-700 text-sm mt-1">
                                Booking is <span className="text-green-600 font-semibold">OPEN</span> for you.
                              </p>
                            ) : (
                              <p className="text-gray-600 text-sm mt-1">
                                Booking will start on{" "}
                                <span className="font-medium text-blue-700">
                                  {info.bookingStart.toLocaleString("en-IN", {
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </p>
                            )}
                          </div>

                          {/* Only show "Complete your profile" notice if profile is INCOMPLETE & room NOT booked */}
                          {!isProfileComplete && !info.isBooked && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                              <p className="font-medium">Complete your profile in Edit Details to book a room</p>
                            </div>
                          )}

                          <div className="flex flex-col gap-2">
                            {info.isBooked ? (
                              <div className="px-6 py-2.5 bg-green-100 text-green-800 font-semibold rounded-lg border border-green-300 text-center shadow-sm">
                                ✅ Your Room is Already Booked
                              </div>
                            ) : info.canBook ? (
                              <button
                                onClick={handleBookRoomClick}
                                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg shadow hover:from-emerald-600 hover:to-green-700 transition-all cursor-pointer font-medium"
                              >
                                🚪 Book a Room
                              </button>
                            ) : (
                              <button
                                disabled
                                className="px-6 py-2.5 rounded-lg bg-gray-200 text-gray-500 cursor-not-allowed font-medium"
                              >
                                Booking Not Available
                              </button>
                            )}

                            {/* Only show "Edit Your Details" button if profile is INCOMPLETE & room NOT booked */}
                            {!isProfileComplete && !info.isBooked && (
                              <Link
                                to="/edit-details"
                                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition text-center font-medium"
                              >
                                ✏️ Edit Your Details
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  )
                )}

                {/* Document Wallet Section */}
                {(() => {
                  const hasRoomAllocation =
                    studentData?.allocation &&
                    ["pending", "reported", "alloted"].includes(
                      studentData.allocation.status?.toLowerCase()
                    );

                  if (hasRoomAllocation) {
                    return <DocumentWallet />;
                  }
                  return null;
                })()}
              </>
            )}
          </main>

          <Footer />
        </div>
      </div>

      {/* Incomplete Profile Modal */}
      {showIncompleteProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-red-600 mb-2">⚠️ Profile Incomplete</h2>
              <p className="text-gray-700 font-semibold">
                You cannot book a room until you fill all your details
              </p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-6">
              <p className="text-red-800 font-semibold mb-2">Missing Fields:</p>
              <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                {blankFieldsList.map((field, idx) => (
                  <li key={idx}>{field}</li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowIncompleteProfileModal(false);
                  navigate("/edit-details");
                }}
                className="w-full px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
              >
                Fill Details Now
              </button>
              <button
                onClick={() => setShowIncompleteProfileModal(false)}
                className="w-full px-4 py-2.5 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DashboardCard({ icon, title, content }) {
  return (
    <div className="bg-white rounded-xl shadow hover:shadow-lg transition p-4 flex flex-col space-y-2">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="text-md font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="text-sm space-y-1">{content}</div>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <p className="text-gray-700">
      <span className="text-gray-500">{label}: </span>
      <span className="font-medium">{value || "N/A"}</span>
    </p>
  );
}

function SingleSeaterRequest() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchStatus() {
      setLoading(true);
      const res = await getSingleSeaterRequestStatus();
      if (res.success) {
        setStatus(res.request);
      } else {
        setStatus(null);
      }
      setLoading(false);
    }
    fetchStatus();
  }, []);

  const handleRequest = async () => {
    if (!window.confirm("Do you want to request for single seater room?"))
      return;

    setSubmitting(true);
    const res = await submitSingleSeaterRequest();
    if (res.success) {
      alert("Request submitted successfully!");
      setStatus(res.request);
    } else {
      setError(res.error || "Failed to submit request.");
    }
    setSubmitting(false);
  };

  if (loading) return <p className="text-gray-500">Loading request status...</p>;

  return (
    <div>
      {status ? (
        <div className="bg-gray-50 p-3 rounded border">
          <p>
            Request Status: <span className="font-medium">{status.status}</span>
          </p>
          <p>Submitted on: {status.requested_at}</p>
          <p>Remarks (If Any): {status.remarks}</p>
          <p>Updated At: {status.updated_at}</p>
        </div>
      ) : (
        <button
          onClick={handleRequest}
          disabled={submitting}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          {submitting ? "Submitting..." : "Request Single Seater Room"}
        </button>
      )}
      {error && <p className="text-red-500 mt-2">{error}</p>}
    </div>
  );
}

function DocumentWallet() {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <h3 className="text-lg font-semibold text-gray-700 mb-3">
        📂 Document Wallet
      </h3>
      <p className="text-gray-600 text-sm mb-3">
        Access important hostel documents here.
      </p>

      <div className="flex flex-col gap-2">
        <Link
          to="/hostel-forms"
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm text-center hover:bg-blue-700 transition"
        >
          📄 Hostel Forms
        </Link>
      </div>
    </div>
  );
}
